import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { formatPercent } from "../../../lib/format";
import {
  Card,
  CardTitle,
  type ChartDatum,
  ChartLegend,
  DonutChart,
  Icon,
  Row,
  Select,
} from "../../ui";
import { MemberBalanceList } from "../components/MemberBalanceList";
import type { Expense, Member, MemberFilter } from "../types";
import { labelForMember } from "../utils";

interface ChartsSectionProps {
  expenses: Expense[];
  categories: Array<{ id: string; name: string; icon: string; color: string }>;
  members: Member[];
  currency: string;
}

export function ChartsSection({ expenses, categories, members, currency }: ChartsSectionProps) {
  const [memberFilter, setMemberFilter] = useState<MemberFilter>("all");

  const breakdownByCategory = useMemo(() => {
    const totals = new Map<string, number>();
    const counts = new Map<string, number>();
    // Orphaned `categoryId` references (the category was deleted but
    // expenses still point at it) must merge into the same bucket as
    // null categoryIds — otherwise both render with the "Ukategorisert"
    // label and collide on React keys downstream.
    const knownCategoryIds = new Set(categories.map((c) => c.id));

    // For "shared" we sum the raw shared amounts (existing behaviour).
    // For "all" we sum every expense (shared + private) at face value.
    // For a specific member we sum that member's actual exposure:
    // their splits (their portion of shared expenses + full amount when private/sole).
    const addAmount = (id: string | null, value: number) => {
      if (value <= 0) return;
      const key = id && knownCategoryIds.has(id) ? id : "uncategorized";
      totals.set(key, (totals.get(key) ?? 0) + value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    };

    if (memberFilter === "shared") {
      for (const e of expenses) {
        if (!e.isShared) continue;
        addAmount(e.categoryId, Number.parseFloat(e.amount));
      }
    } else if (memberFilter === "all") {
      for (const e of expenses) {
        addAmount(e.categoryId, Number.parseFloat(e.amount));
      }
    } else {
      // Specific member: show every expense *they* have in the settlement,
      // i.e. the ones they paid for — shared OR private, full amount. We
      // intentionally do NOT pull in shared expenses paid by others
      // (those belong to that payer's column, not this one).
      const userId = memberFilter;
      for (const e of expenses) {
        if (e.paidByUserId !== userId) continue;
        addAmount(e.categoryId, Number.parseFloat(e.amount));
      }
    }

    return [...totals.entries()]
      .map(([id, value]): ChartDatum & { count: number } => {
        const cat = categories.find((c) => c.id === id);
        return {
          label: cat?.name ?? t.settlement.charts.uncategorized,
          value,
          color: cat?.color,
          count: counts.get(id) ?? 0,
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [expenses, categories, memberFilter]);

  const sharedByCategory = breakdownByCategory;

  // Per-member: how much they laid out, split by shared vs private.
  // The card visualizes the proportion — settlement math lives in the
  // "Oppgjør" section below.
  const memberStats = useMemo(() => {
    const paidShared = new Map<string, number>();
    const paidPrivate = new Map<string, number>();
    for (const e of expenses) {
      const amount = Number.parseFloat(e.amount);
      const target = e.isShared ? paidShared : paidPrivate;
      target.set(e.paidByUserId, (target.get(e.paidByUserId) ?? 0) + amount);
    }
    return members
      .map((m) => {
        const ps = paidShared.get(m.userId) ?? 0;
        const pp = paidPrivate.get(m.userId) ?? 0;
        return { member: m, paid: ps + pp, paidShared: ps, paidPrivate: pp };
      })
      .filter((s) => s.paid > 0)
      .sort((a, b) => b.paid - a.paid);
  }, [expenses, members]);

  const sharedTotal = sharedByCategory.reduce((s, d) => s + d.value, 0);
  const sharedCount = sharedByCategory.reduce((s, d) => s + d.count, 0);
  const topCategory = sharedByCategory[0];

  const selectedMember =
    memberFilter !== "all" && memberFilter !== "shared"
      ? (members.find((m) => m.userId === memberFilter) ?? null)
      : null;
  const filterHint = selectedMember
    ? t.settlement.charts.memberFilterPersonalHint(
        selectedMember.displayName ?? selectedMember.email.split("@")[0] ?? selectedMember.email,
      )
    : memberFilter === "all"
      ? t.settlement.charts.memberFilterAllHint
      : t.settlement.charts.memberFilterSharedHint;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card padding="md">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2">
            <Icon name="pie-chart" size="sm" className="text-ink-500" />
            {t.settlement.charts.sharedByCategoryTitle}
          </CardTitle>
          <div className="inline-flex items-center gap-2 text-ink-500 text-xs">
            <span className="hidden sm:inline">{t.settlement.charts.memberFilterLabel}</span>
            <Select
              value={memberFilter}
              onChange={(e) => setMemberFilter(e.target.value as MemberFilter)}
              className="h-8 text-xs"
              aria-label={t.settlement.charts.memberFilterLabel}
            >
              <option value="all">{t.settlement.charts.memberFilterAll}</option>
              <option value="shared">{t.settlement.charts.memberFilterSharedOnly}</option>
              {members.map((m) => {
                const lbl = labelForMember(m, members);
                return (
                  <option key={m.userId} value={m.userId} title={lbl.tooltip}>
                    {lbl.name}
                  </option>
                );
              })}
            </Select>
          </div>
        </div>
        <p className="mb-3 text-ink-500 text-xs">{filterHint}</p>
        {sharedByCategory.length === 0 ? (
          <p className="text-ink-400 text-sm">{t.settlement.charts.sharedByCategoryEmpty}</p>
        ) : (
          <>
            <Row gap={5} align="center" className="flex-col sm:flex-row">
              <DonutChart
                data={sharedByCategory}
                size={140}
                centerLabel={
                  sharedTotal >= 10000
                    ? `${Math.round(sharedTotal / 1000)}k`
                    : Math.round(sharedTotal).toString()
                }
                centerHint={t.settlement.charts.sharedCountHint(sharedCount)}
              />
              <ChartLegend
                data={sharedByCategory}
                showPercent
                currency={currency}
                className="min-w-0 flex-1"
              />
            </Row>
            {topCategory && sharedByCategory.length > 1 && sharedTotal > 0 && (
              <p className="mt-4 border-ink-100 border-t pt-3 text-ink-500 text-xs">
                {t.settlement.charts.topCategoryHint(
                  topCategory.label,
                  formatPercent(topCategory.value / sharedTotal),
                )}
              </p>
            )}
          </>
        )}
      </Card>

      <Card padding="md">
        <CardTitle className="mb-4 flex items-center gap-2">
          <Icon name="users" size="sm" className="text-ink-500" />
          {t.settlement.charts.perMemberTitle}
        </CardTitle>
        {memberStats.length === 0 ? (
          <p className="text-ink-400 text-sm">{t.settlement.charts.perMemberEmpty}</p>
        ) : (
          <MemberBalanceList stats={memberStats} members={members} currency={currency} />
        )}
      </Card>
    </div>
  );
}
